import * as React from 'react'
import { useParams } from 'react-router-dom';
import { Team, Player, Contract, PlayerPositionEnum, ContractStatus } from '../../../sdk/model';
import { MaterialReactTable } from 'material-react-table';
import {Box, Typography, Button, Modal, Card, CardContent, TextField} from '@mui/material';
import { toSalary } from '../../util/SalaryDisplay';
import ContractDetails from '../contract/ContractDetails';
import { contractApi } from '../../../adapters/APIExporter';
import TeamDisplay from '../team/TeamDisplay'
import PlayerCardLink from '../../cards/playerCardLink';
import MoneyFormat from '../../util/MoneyFormat';

const positionOrder: Array<PlayerPositionEnum> = [PlayerPositionEnum.Qb,
                                                  PlayerPositionEnum.Rb,
                                                  PlayerPositionEnum.Wr,
                                                  PlayerPositionEnum.Te
                                                  ];

interface deadCapReportProps {
  deadCapContractIds: string[]
  teamId: string,
  currentSeason: number,
  teamMap: Map<string | undefined, Team | undefined> | undefined,
  playerMap: Map<string | undefined, Player | undefined>,
  commish: boolean
}

function DeadCapReport(props: deadCapReportProps) {
  const TOTAL_SEASONS_TO_DISPLAY = 5;
  
  const {leagueId} = useParams();
  const [deadCapContracts, setDeadCapContracts] = React.useState<Contract[]>([]);
  const [contractToEdit, setContractToEdit] = React.useState<Contract | undefined>(undefined);
  const [deadCapAmount, setDeadCapAmount] = React.useState(0)
  
  React.useEffect(() => {
    contractApi.getContractsByIds(props.deadCapContractIds).then(response => {
      setDeadCapContracts(response.data.filter(contract => contract.playerId));
    })
  }, [props.deadCapContractIds]);
  
  if (deadCapContracts.length === 0) {
    return <p>Loading...</p>
  }
  const columns = [
      {
        accessorKey: 'position',
        header: 'Position',
        size: 5,
        sortingFn: (rowA, rowB) => {
          return positionOrder.indexOf(rowA.getValue('position')) - positionOrder.indexOf(rowB.getValue('position'));
          }
      },
      {
        accessorKey: 'name',
        header: 'Player Name',
        size: 50,
        Cell: ({cell, row}) => (
          <PlayerCardLink player={row.original.player} leagueId={leagueId!} />
        )
      }
  ];
  {Array.from(Array(TOTAL_SEASONS_TO_DISPLAY).keys()).forEach(seasonIndex =>
    columns.push({
      accessorKey: 'capHit.' + seasonIndex,
      header: 'Cap Hit in ' + (props.currentSeason + seasonIndex),
      size: 2,
      Cell : ({cell}) => (
        cell.getValue() ? toSalary(cell.getValue()) : '$0'
      )
    })
  )}
  columns.push({
    accessorKey: 'status',
    header: 'Current Status',
    size: 50,
  });
  
  const data = deadCapContracts.map(contract => {
    const player: Player = props.playerMap.get(contract.playerId)!;
    const capHit = contract.seasons!.filter(season => season.season_year! >= props.currentSeason).map(season => season.weeks?.reduce((seasonSum, week) => 
            seasonSum + (week.payments?.filter(payment => props.teamId === payment.teamId)?.reduce((weeklySum, payment) => weeklySum + (payment?.price || 0), 0) || 0)
          , 0));
    return {
      position: player.position,
      name: player.abbr_name,
      player: player,
      contract: contract,
      capHit: capHit,
      status: ContractStatus.Active === contract.status ? <React.Fragment>Signed to <TeamDisplay leagueId={leagueId!} team={props.teamMap?.get(contract.teamId)!} emphasized={false} teamId={contract.teamId} /></React.Fragment> : contract.status
    }
  });
  
  const playerContractToEdit = contractToEdit ? props.playerMap.get(contractToEdit.playerId) : undefined;
  
  function updateDeadCap() {
    contractApi.updateDeadCap(contractToEdit?.id!, props.teamId, deadCapAmount).then(() => setContractToEdit(undefined))
  }
  
  return <Box>
    <Modal open={contractToEdit !== undefined} onClose={() => setContractToEdit(undefined)} 
        aria-labelledby="Add Player Modal" aria-describedby="Add player to selected team"
        sx={{margin: '5%'}}>
      <Card style={{maxHeight: 800, overflow: 'auto'}}><CardContent>
        <Typography>Edit {props.currentSeason} Dead Cap for <PlayerCardLink player={playerContractToEdit} leagueId={leagueId!} /></Typography>
        <TextField name="deadCapAmount" label={props.currentSeason + " Dead Cap"} value={deadCapAmount}
                        InputProps={{inputProps: {min: 0, max: 500000000, step: 100000}, inputComponent: MoneyFormat as any}} onChange={event => setDeadCapAmount(Number(event.target.value))}/>
        <Button className='capsized-button' onClick={updateDeadCap}>Save</Button>
      </CardContent></Card>
    </Modal>
    <Typography variant="h5">
      Dead Cap:
    </Typography>
    <MaterialReactTable columns={columns} data={data} enableFilters={false} enableColumnActions={false} enablePagination={false} 
        initialState={{
          density: 'compact',
          sorting: [
            {
              id: 'position',
              desc: false
            },
            {
              id: 'capHit',
              desc: true
            }
          ]
        }} 
        renderDetailPanel={({row}) =><ContractDetails leagueId={leagueId!} player={row.original.player} contract={row.original.contract!} />}
        enableRowActions={props.commish}
        renderRowActions={({ row }) => {
          return <Button className="capsized-button" onClick={() => {
            setContractToEdit(row.original.contract);
            setDeadCapAmount(row.original.capHit[0])
          }}>Edit</Button>
        }} 
       />
  </Box>
}

export default DeadCapReport;